import type {
  Conversation,
  ConversationSummary,
  Message,
  Snippet,
  Template,
  UserProfile,
} from '@quill/shared';

const DEMO_USER: UserProfile = {
  id: 'demo-user-ayesha',
  email: 'ayesha@quill.local',
  name: 'Ayesha Iqbal',
  createdAt: new Date('2026-06-01T09:00:00Z').toISOString(),
};

const templates: Template[] = [
  {
    id: 't1',
    key: 'summarise-short',
    label: 'Summarise',
    intent: 'summarise',
    body: 'Paste a long passage and Quill picks the strongest sentences.',
  },
  {
    id: 't2',
    key: 'rewrite-friendly',
    label: 'Rewrite — friendly',
    intent: 'rewrite',
    body: 'Soften the tone for a colleague or a friend.',
  },
  {
    id: 't3',
    key: 'rewrite-professional',
    label: 'Rewrite — professional',
    intent: 'rewrite',
    body: 'Tighten the wording for a formal email.',
  },
  {
    id: 't4',
    key: 'explain-simply',
    label: 'Explain simply',
    intent: 'explain',
    body: 'Trade long words for plain ones and split overlong sentences.',
  },
  {
    id: 't5',
    key: 'checklist',
    label: 'Make a checklist',
    intent: 'checklist',
    body: 'Turn notes into a clear list of tasks.',
  },
  {
    id: 't6',
    key: 'draft-reply',
    label: 'Draft a reply',
    intent: 'draft',
    body: 'Generate a polite, ready-to-edit reply.',
  },
  {
    id: 't7',
    key: 'actions',
    label: 'Pull out action items',
    intent: 'actions',
    body: 'Lift the tasks hiding inside a wall of text.',
  },
];

type State = {
  conversations: Map<string, Conversation>;
  snippets: Map<string, Snippet>;
};

const initialState = (): State => {
  const conversations = new Map<string, Conversation>();
  const welcome: Conversation = {
    id: 'demo-conv-welcome',
    title: 'Welcome to Quill',
    createdAt: new Date('2026-06-10T08:00:00Z').toISOString(),
    messages: [
      {
        id: 'demo-msg-1',
        role: 'user',
        text: 'Help me turn the weekly update into a short summary.',
        intent: 'summarise',
        at: new Date('2026-06-10T08:00:00Z').toISOString(),
      },
      {
        id: 'demo-msg-2',
        role: 'assistant',
        text: 'Paste the update below, then tap Summarise. I will pick the strongest sentences.',
        intent: 'summarise',
        at: new Date('2026-06-10T08:00:10Z').toISOString(),
      },
    ],
  };
  const planning: Conversation = {
    id: 'demo-conv-planning',
    title: 'Bilal’s rota for next week',
    createdAt: new Date('2026-06-12T14:30:00Z').toISOString(),
    messages: [
      {
        id: 'demo-msg-3',
        role: 'user',
        text: 'Bilal needs to confirm the cover shift. Hamza will lead the Sunday standup. Order the new badges before Friday.',
        intent: 'checklist',
        at: new Date('2026-06-12T14:30:00Z').toISOString(),
      },
      {
        id: 'demo-msg-4',
        role: 'assistant',
        text: '- Confirm the cover shift with Bilal\n- Lead the Sunday standup with Hamza\n- Order the new badges before Friday',
        intent: 'checklist',
        at: new Date('2026-06-12T14:30:05Z').toISOString(),
      },
    ],
  };
  conversations.set(welcome.id, welcome);
  conversations.set(planning.id, planning);

  const snippets = new Map<string, Snippet>();
  const seedSnippet: Snippet = {
    id: 'demo-snip-1',
    title: 'Polite follow-up to a vendor',
    body: 'Following up on the catering quote from earlier. Is there anything blocking a decision? Glad to help unblock it.',
    createdAt: new Date('2026-06-13T11:15:00Z').toISOString(),
  };
  snippets.set(seedSnippet.id, seedSnippet);

  return { conversations, snippets };
};

const state = initialState();

const newId = (() => {
  let counter = 1000;
  return (prefix: string) => `${prefix}-${counter++}`;
})();

const toSummary = (conversation: Conversation): ConversationSummary => {
  const last = conversation.messages[conversation.messages.length - 1];
  return {
    id: conversation.id,
    title: conversation.title,
    createdAt: conversation.createdAt,
    preview: last ? last.text.slice(0, 140) : null,
  };
};

const sortedConversations = (): ConversationSummary[] =>
  [...state.conversations.values()]
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))
    .map(toSummary);

const sortedSnippets = (): Snippet[] =>
  [...state.snippets.values()].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));

type Body = Record<string, unknown>;
const readString = (body: Body | undefined, key: string): string => {
  const value = body?.[key];
  if (typeof value !== 'string') throw new Error(`Missing ${key}`);
  return value;
};

export const DEMO_IDENTITY = {
  user: DEMO_USER,
  accessToken: 'demo-access-token',
  refreshToken: 'demo-refresh-token',
};

export const handleDemoRequest = <T>(method: string, path: string, body: unknown): T => {
  const payload = (body ?? {}) as Body;
  const route = `${method} ${path}`;

  if (route === 'POST /auth/login' || route === 'POST /auth/register') {
    return DEMO_IDENTITY as unknown as T;
  }
  if (route === 'POST /auth/refresh') {
    return {
      accessToken: DEMO_IDENTITY.accessToken,
      refreshToken: DEMO_IDENTITY.refreshToken,
    } as unknown as T;
  }
  if (route === 'POST /auth/logout') {
    return { revoked: true } as unknown as T;
  }
  if (route === 'GET /auth/me') {
    return DEMO_USER as unknown as T;
  }
  if (route === 'GET /templates') {
    return templates as unknown as T;
  }
  if (route === 'GET /conversations') {
    return sortedConversations() as unknown as T;
  }
  if (route === 'POST /conversations') {
    const conversation: Conversation = {
      id: newId('demo-conv'),
      title: readString(payload, 'title'),
      createdAt: new Date().toISOString(),
      messages: [],
    };
    state.conversations.set(conversation.id, conversation);
    return conversation as unknown as T;
  }

  const conversationMatch = path.match(/^\/conversations\/([^/]+)(\/messages)?$/);
  if (conversationMatch) {
    const id = conversationMatch[1] ?? '';
    const isMessages = conversationMatch[2] === '/messages';
    const conversation = state.conversations.get(id);
    if (!conversation) throw new Error('That conversation is not available.');

    if (method === 'GET' && !isMessages) {
      return conversation as unknown as T;
    }
    if (method === 'PATCH' && !isMessages) {
      conversation.title = readString(payload, 'title');
      return conversation as unknown as T;
    }
    if (method === 'DELETE' && !isMessages) {
      state.conversations.delete(id);
      return { deleted: true } as unknown as T;
    }
    if (method === 'POST' && isMessages) {
      const message: Message = {
        id: newId('demo-msg'),
        role: readString(payload, 'role') as Message['role'],
        text: readString(payload, 'text'),
        intent: typeof payload['intent'] === 'string' ? (payload['intent'] as string) : null,
        at: new Date().toISOString(),
      };
      conversation.messages.push(message);
      return message as unknown as T;
    }
  }

  if (route === 'GET /snippets') {
    return sortedSnippets() as unknown as T;
  }
  if (route === 'POST /snippets') {
    const snippet: Snippet = {
      id: newId('demo-snip'),
      title: readString(payload, 'title'),
      body: readString(payload, 'body'),
      createdAt: new Date().toISOString(),
    };
    state.snippets.set(snippet.id, snippet);
    return snippet as unknown as T;
  }

  const snippetMatch = path.match(/^\/snippets\/([^/]+)$/);
  if (snippetMatch) {
    const id = snippetMatch[1] ?? '';
    const snippet = state.snippets.get(id);
    if (!snippet) throw new Error('That snippet is not available.');

    if (method === 'PUT') {
      const next: Snippet = {
        ...snippet,
        title: typeof payload['title'] === 'string' ? payload['title'] : snippet.title,
        body: typeof payload['body'] === 'string' ? payload['body'] : snippet.body,
      };
      state.snippets.set(id, next);
      return next as unknown as T;
    }
    if (method === 'DELETE') {
      state.snippets.delete(id);
      return { deleted: true } as unknown as T;
    }
  }

  throw new Error(`Demo route not handled: ${route}`);
};
